import { useEffect, useMemo, useState } from 'react'
import { Dialog } from 'radix-ui'
import { X } from 'lucide-react'
import { Combobox } from '@/components/common/Combobox'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useAllDocumentTypes } from '@/features/admin/document-types/hooks'
import { useConfigurations } from '@/features/admin/configurations/hooks'
import { useCreateField, useUpdateField } from '../hooks'
import type { FieldDefinition, FieldOption, FieldType, FormSchema, SemanticRole, ValidationRules } from '../types'
import { FIELD_TYPE_LABELS, OPTIONS_TYPES, SEMANTIC_ROLE_LABELS, compatibleSemanticRoles } from '../types'
import type { VerificationMode } from '@/features/contract/types'
import { FieldVerificationConfig } from './FieldVerificationConfig'
import { SectionCheckboxList } from './SectionCheckboxList'

const CODE_REGEX = /^[a-zA-Z][a-zA-Z0-9_]{0,63}$/
// SEPARATOR/SPACER są zwykle dodawane osobnymi przyciskami w toolbarze sekcji.
// Tu listujemy je na końcu - widoczne w dropdownie dla edycji istniejącego
// separatora/spacera (typu i tak nie da się zmienić - mode='edit' blokuje).
const FIELD_TYPES: FieldType[] = [
  'TEXT', 'TEXTAREA', 'RICH_TEXT',
  'NUMBER', 'CURRENCY', 'DATE', 'BOOLEAN',
  'SELECT', 'MULTI_SELECT', 'CHECKBOX_GROUP',
  'EMAIL', 'PHONE', 'URL',
  'IBAN', 'NIP', 'PESEL',
  'REFERENCE',
  'FILE', 'GALLERY',
  'SEPARATOR', 'SPACER',
]

type Mode =
  | { kind: 'create'; presetSectionCode?: string }
  | { kind: 'edit'; field: FieldDefinition }

export function useFieldFormSheet(objectType: string, schema: FormSchema | undefined) {
  const [mode, setMode] = useState<Mode | null>(null)
  return {
    /** Opens the new-field sheet. Pass a sectionCode to pre-check that section. */
    openCreate: (presetSectionCode?: string) => setMode({ kind: 'create', presetSectionCode }),
    openEdit: (field: FieldDefinition) => setMode({ kind: 'edit', field }),
    sheet: mode && schema && (
      <FieldFormSheet
        objectType={objectType}
        schema={schema}
        mode={mode}
        onClose={() => setMode(null)}
      />
    ),
  }
}

function FieldFormSheet({ objectType, schema, mode, onClose }: {
  objectType: string
  schema: FormSchema
  mode: Mode
  onClose: () => void
}) {
  const create = useCreateField(objectType)
  const update = useUpdateField(objectType)

  const sections = schema.sections
  const initial = mode.kind === 'edit' ? mode.field : null

  const [fieldCode, setFieldCode] = useState(initial?.fieldCode ?? '')
  const presetSectionCode = mode.kind === 'create' ? mode.presetSectionCode : undefined

  // Derive initial sectionCodes from placements when editing.
  const initialSectionCodes = useMemo((): string[] => {
    if (mode.kind === 'create') {
      return presetSectionCode ? [presetSectionCode] : []
    }
    // Edit mode: find all placements for this field, then map sectionId -> section.code
    const sectionById = new Map(schema.sections.map(s => [s.id, s]))
    return (schema.placements ?? [])
      .filter(p => p.fieldId === mode.field.id)
      .map(p => sectionById.get(p.sectionId)?.code)
      .filter((c): c is string => c != null)
  }, [mode, schema.placements, schema.sections, presetSectionCode])

  const [sectionCodes, setSectionCodes] = useState<string[]>(initialSectionCodes)

  // Re-sync sectionCodes when mode/schema changes (e.g. fresh server data after sheet mount).
  useEffect(() => {
    setSectionCodes(initialSectionCodes)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode.kind === 'edit' ? mode.field.id : 'create', schema.placements])

  const [labelPl, setLabelPl] = useState(initial?.labelPl ?? '')
  const [fieldType, setFieldType] = useState<FieldType>(initial?.fieldType ?? 'TEXT')
  const [isRequired, setIsRequired] = useState(initial?.isRequired ?? false)
  const [isActive, setIsActive] = useState(initial?.isActive ?? true)
  const [helpText, setHelpText] = useState<string>(initial?.helpText ?? '')
  const [documentTypeId, setDocumentTypeId] = useState<number | null>(initial?.documentTypeId ?? null)
  const [semanticRole, setSemanticRole] = useState<SemanticRole | null>(
    (initial?.semanticRole as SemanticRole | undefined) ?? null,
  )

  // Re-sync gdy initial się zmienia (np. cache schema dostaje fresh response z
  // backendu już po mount sheetu) - useState(initial) używa wartości tylko przy
  // pierwszym renderze, bez tego state zostaje przy starym/null pomimo świeżych
  // danych z serwera.
  useEffect(() => {
    setSemanticRole((initial?.semanticRole as SemanticRole | undefined) ?? null)
  }, [initial?.semanticRole])

  // Role kompatybilne z object_type ORAZ typem pola (produkt → ELEMENT_PRICE, umowa → role umowy).
  const compatibleRoles = useMemo<SemanticRole[]>(
    () => compatibleSemanticRoles(objectType, fieldType),
    [objectType, fieldType],
  )

  // Gdy admin zmieni typ pola tak, że bieżąca rola staje się niekompatybilna - czyścimy.
  useEffect(() => {
    if (semanticRole && !compatibleRoles.includes(semanticRole)) {
      setSemanticRole(null)
    }
  }, [compatibleRoles, semanticRole])

  // Doc-types ze wszystkich produktów - potrzebne tylko gdy fieldType=FILE.
  const productsQ = useConfigurations(false)
  const configurationCodes = useMemo(() => productsQ.data?.map(p => p.code), [productsQ.data])
  const docTypesQ = useAllDocumentTypes(configurationCodes, true)
  const productByCode = useMemo(() => {
    const m = new Map<string, string>()
    productsQ.data?.forEach(p => m.set(p.code, p.labelPl))
    return m
  }, [productsQ.data])
  const [validation, setValidation] = useState<ValidationRules>({
    min: initial?.validation?.min ?? null,
    max: initial?.validation?.max ?? null,
    regex: initial?.validation?.regex ?? null,
    maxLength: initial?.validation?.maxLength ?? null,
  })
  const [options, setOptions] = useState<FieldOption[]>(initial?.options ?? [])
  const [referenceTable, setReferenceTable] = useState<string>(initial?.referenceTable ?? '')
  const [verificationModes, setVerificationModes] = useState<VerificationMode[]>(
    (initial?.verificationModes as VerificationMode[] | undefined) ?? [],
  )
  const [verifierRoleCodes, setVerifierRoleCodes] = useState<string[]>(initial?.verifierRoleCodes ?? [])
  const [error, setError] = useState<string | null>(null)

  useEffect(() => { setError(null) }, [mode])

  const submit = () => {
    setError(null)
    if (mode.kind === 'create' && !CODE_REGEX.test(fieldCode)) {
      setError('Kod pola: litery, cyfry, podkreślenia. Musi zaczynać się od litery.')
      return
    }
    if (!labelPl.trim()) { setError('Etykieta nie może być pusta.'); return }
    if (sectionCodes.length === 0) { setError('Wybierz co najmniej jedną sekcję - pole bez sekcji nie wyświetla się na formularzu.'); return }
    const needsOptions = OPTIONS_TYPES.includes(fieldType)
    if (needsOptions && options.length === 0) {
      setError(`${FIELD_TYPE_LABELS[fieldType]} wymaga co najmniej jednej opcji.`)
      return
    }
    if (fieldType === 'REFERENCE' && !referenceTable.trim()) {
      setError('Referencja wymaga reference_table.')
      return
    }
    if ((fieldType === 'FILE' || fieldType === 'GALLERY') && documentTypeId == null) {
      setError('Pole typu Plik / Galeria wymaga przypisanego typu dokumentu.')
      return
    }
    if ((verificationModes.length > 0) !== (verifierRoleCodes.length > 0)) {
      setError('Weryfikacja: tryby i role muszą być wybrane razem (lub oba puste).')
      return
    }

    const payload = {
      sectionCodes,
      labelPl,
      fieldType,
      isRequired,
      isFilterable: false,
      validation: hasValidation(validation) ? validation : null,
      options: needsOptions ? options : null,
      referenceTable: fieldType === 'REFERENCE' ? referenceTable : null,
      helpText: helpText.trim() === '' ? null : helpText,
      documentTypeId: (fieldType === 'FILE' || fieldType === 'GALLERY') ? documentTypeId : null,
      semanticRole,
      verificationModes,
      verifierRoleCodes,
      isActive,
    }

    if (mode.kind === 'create') {
      create.mutate(
        { ...payload, fieldCode },
        { onSuccess: onClose, onError: e => setError(formatError(e)) },
      )
    } else {
      update.mutate(
        { fieldCode: mode.field.fieldCode, payload },
        { onSuccess: onClose, onError: e => setError(formatError(e)) },
      )
    }
  }

  const isSubmitting = create.isPending || update.isPending

  const inputCls = "h-8 w-full rounded-md border border-slate-300 bg-white px-2.5 text-sm text-slate-900 placeholder:text-slate-400 shadow-xs transition-colors focus:outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-500/15 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:opacity-90"
  const labelCls = "text-[11px] font-semibold uppercase tracking-[0.06em] text-slate-600"
  const helperCls = "text-[11px] text-slate-400"

  return (
    <Dialog.Root open onOpenChange={o => { if (!o) onClose() }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40 backdrop-blur-[2px]" />
        <Dialog.Content className="fixed right-0 top-0 z-50 h-full w-[560px] max-w-full bg-white shadow-2xl flex flex-col">
          {/* Sticky header */}
          <header className="px-6 py-3 border-b border-slate-200 flex items-start justify-between gap-4 shrink-0">
            <div className="min-w-0">
              <Dialog.Title className="text-lg font-semibold tracking-tight text-slate-900 truncate">
                {mode.kind === 'create' ? 'Nowe pole' : 'Edycja pola'}
              </Dialog.Title>
              <Dialog.Description className="text-xs text-slate-500 font-mono truncate mt-0.5">
                {mode.kind === 'create' ? 'Dodajesz pole do schematu formularza' : mode.field.fieldCode}
              </Dialog.Description>
            </div>
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="size-8 rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-700 flex items-center justify-center shrink-0"
              aria-label="Zamknij"
            >
              <X size={16} />
            </button>
          </header>

          {/* Scrollable body with tabs */}
          <div className="flex-1 overflow-auto flex flex-col">
            <Tabs defaultValue="basic" className="flex flex-col flex-1 gap-0">
              <TabsList className="rounded-none w-full justify-start gap-1 px-6 border-b border-slate-200 bg-white h-auto py-0 shadow-none">
                <TabsTrigger
                  value="basic"
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-sky-500 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-sky-700 text-slate-600 px-3 py-2.5 text-sm font-medium"
                >
                  Podstawowe
                </TabsTrigger>
                <TabsTrigger
                  value="advanced"
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-sky-500 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-sky-700 text-slate-600 px-3 py-2.5 text-sm font-medium"
                >
                  Zaawansowane
                </TabsTrigger>
              </TabsList>

              {/* Tab: Podstawowe */}
              <TabsContent value="basic" className="flex-1 px-6 py-3 flex flex-col gap-3 mt-0">
                <section className="flex flex-col gap-2">
                  <label className="grid gap-1">
                    <span className="text-xs font-medium text-slate-700">Kod pola</span>
                    <input
                      value={fieldCode}
                      onChange={e => setFieldCode(e.target.value)}
                      disabled={mode.kind === 'edit' || isSubmitting}
                      className={`${inputCls} font-mono`}
                      placeholder="totalAmount"
                    />
                  </label>

                  <div className="grid gap-1">
                    <span className="text-xs font-medium text-slate-700">
                      Sekcje
                      <span className="ml-1 text-[10px] font-normal text-slate-400">(pole może być w wielu sekcjach)</span>
                    </span>
                    <SectionCheckboxList
                      sections={sections}
                      selected={sectionCodes}
                      onChange={setSectionCodes}
                      disabled={isSubmitting}
                    />
                  </div>

                  <label className="grid gap-1">
                    <span className="text-xs font-medium text-slate-700">Etykieta</span>
                    <input
                      value={labelPl}
                      onChange={e => setLabelPl(e.target.value)}
                      disabled={isSubmitting}
                      className={inputCls}
                    />
                  </label>

                  <label className="grid gap-1">
                    <span className="text-xs font-medium text-slate-700">Typ pola</span>
                    <Combobox<string>
                      value={fieldType}
                      onChange={v => setFieldType((v ?? 'TEXT') as FieldType)}
                      options={FIELD_TYPES.map(t => ({ value: t, label: FIELD_TYPE_LABELS[t] }))}
                      disabled={mode.kind === 'edit' || isSubmitting}
                    />
                    {mode.kind === 'edit' && (
                      <span className={helperCls}>Zmiana typu pola jest niedozwolona (utracone dane).</span>
                    )}
                  </label>
                </section>

                {/* Flagi - dwie obok siebie */}
                <section className="grid grid-cols-2 gap-2 border-t border-slate-100 pt-2">
                  <label
                    className="flex items-center justify-between gap-2 rounded-md border border-slate-200 bg-slate-50/40 px-3 py-1.5 cursor-pointer hover:bg-slate-50"
                    title="Walidator nie pozwoli zapisać sekcji z pustą wartością"
                  >
                    <span className="text-sm font-medium text-slate-900">Wymagane</span>
                    <input
                      type="checkbox"
                      checked={isRequired}
                      onChange={e => setIsRequired(e.target.checked)}
                      disabled={isSubmitting}
                      className="size-4 accent-sky-600"
                    />
                  </label>
                  <label
                    className="flex items-center justify-between gap-2 rounded-md border border-slate-200 bg-slate-50/40 px-3 py-1.5 cursor-pointer hover:bg-slate-50"
                    title="Nieaktywne pole jest ukryte przed użytkownikami"
                  >
                    <span className="text-sm font-medium text-slate-900">Aktywne</span>
                    <input
                      type="checkbox"
                      checked={isActive}
                      onChange={e => setIsActive(e.target.checked)}
                      disabled={isSubmitting}
                      className="size-4 accent-sky-600"
                    />
                  </label>
                </section>
              </TabsContent>

              {/* Tab: Zaawansowane */}
              <TabsContent value="advanced" className="flex-1 px-6 py-3 flex flex-col gap-3 mt-0">
                {/* Walidacja - kontekstowa */}
                {(fieldType === 'NUMBER' || fieldType === 'CURRENCY' || fieldType === 'TEXT' || fieldType === 'TEXTAREA' || fieldType === 'RICH_TEXT') && (
                  <section className="flex flex-col gap-2">
                    <ValidationFields
                      fieldType={fieldType}
                      validation={validation}
                      onChange={setValidation}
                      disabled={isSubmitting}
                    />
                  </section>
                )}

                {/* Opcje SELECT */}
                {OPTIONS_TYPES.includes(fieldType) && (
                  <section className="flex flex-col gap-2">
                    <OptionsEditor options={options} onChange={setOptions} disabled={isSubmitting} />
                  </section>
                )}

                {/* Reference */}
                {fieldType === 'REFERENCE' && (
                  <section className="flex flex-col gap-2">
                    <h3 className={labelCls}>Referencja</h3>
                    <label className="grid gap-1">
                      <span className="text-xs font-medium text-slate-700">Reference table</span>
                      <input
                        value={referenceTable}
                        onChange={e => setReferenceTable(e.target.value)}
                        disabled={isSubmitting}
                        className={`${inputCls} font-mono`}
                        placeholder="partners.customer"
                      />
                    </label>
                  </section>
                )}

                {/* Plik / Galeria */}
                {(fieldType === 'FILE' || fieldType === 'GALLERY') && (
                  <section className="flex flex-col gap-2">
                    <h3 className={labelCls}>Plik / Załącznik</h3>
                    <label className="grid gap-1">
                      <span className="text-xs font-medium text-slate-700">Typ dokumentu <span className="text-rose-500">*</span></span>
                      <Combobox<number>
                        value={documentTypeId}
                        onChange={v => setDocumentTypeId(v)}
                        options={(docTypesQ.data ?? []).map(dt => ({
                          value: dt.id,
                          label: `[${productByCode.get(dt.configurationCode) ?? dt.configurationCode}] ${dt.labelPl}${dt.acceptedExtensions ? ` (${dt.acceptedExtensions})` : ''}`,
                        }))}
                        emptyOptionLabel="— wybierz typ dokumentu —"
                        placeholder="Wybierz typ dokumentu..."
                        disabled={isSubmitting}
                      />
                      <span className={helperCls}>
                        Plik wgrany przez to pole zostanie zapisany jako załącznik tego typu i pojawi się w zakładce Pliki umowy.
                        Rozszerzenia konfigurujesz w <a href="/settings/configurations" className="underline text-sky-700">Produkty › Typy dokumentów</a>.
                      </span>
                    </label>
                  </section>
                )}

                {/* Pomoc dla operatora */}
                <section className="flex flex-col gap-2 border-t border-slate-100 pt-2">
                  <h3 className={labelCls}>
                    {fieldType === 'INSTRUCTION' ? 'Treść instrukcji' : 'Pomoc dla operatora'}
                  </h3>
                  <textarea
                    value={helpText}
                    onChange={e => setHelpText(e.target.value)}
                    disabled={isSubmitting}
                    rows={fieldType === 'INSTRUCTION' ? 4 : 2}
                    className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-xs focus:outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-500/15 resize-y disabled:bg-slate-50"
                    placeholder={
                      fieldType === 'INSTRUCTION'
                        ? 'Tekst widoczny w formularzu jako info-box…'
                        : 'Pojawi się jako "?" obok labela…'
                    }
                  />
                </section>

                {/* Rola semantyczna (object_type-aware: produkt → ELEMENT_PRICE, umowa → role umowy) */}
                {compatibleRoles.length > 0 && (
                  <section className="flex flex-col gap-2 border-t border-slate-100 pt-2">
                    <h3 className={labelCls}>Rola semantyczna</h3>
                    <label className="grid gap-1">
                      <span className="text-xs font-medium text-slate-700">Rola semantyczna</span>
                      <Combobox<string>
                        value={semanticRole}
                        onChange={v => setSemanticRole(v as SemanticRole | null)}
                        options={compatibleRoles.map(r => ({ value: r, label: SEMANTIC_ROLE_LABELS[r] }))}
                        emptyOptionLabel="— Brak (zwykłe pole dynamiczne) —"
                        placeholder="— Brak (zwykłe pole dynamiczne) —"
                        disabled={isSubmitting}
                      />
                      <span className={helperCls}>
                        Wartość pola jest interpretowana przez system wg roli (np. kwota umowy → kolumna umowy,
                        cena produktu → cena elementu). Tylko jedno pole per rola w danym object_type.
                      </span>
                    </label>
                  </section>
                )}

                {/* Weryfikacja (F-VER) */}
                <FieldVerificationConfig
                  modes={verificationModes}
                  onModesChange={setVerificationModes}
                  roleCodes={verifierRoleCodes}
                  onRoleCodesChange={setVerifierRoleCodes}
                  disabled={isSubmitting}
                />

              </TabsContent>
            </Tabs>
          </div>

          {/* Error area above footer */}
          {error && (
            <div className="px-6 py-2 border-t border-rose-100 bg-rose-50">
              <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
                {error}
              </div>
            </div>
          )}

          {/* Sticky footer */}
          <footer className="px-6 py-2 border-t border-slate-200 bg-slate-50/60 flex justify-end gap-2 shrink-0">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              Anuluj
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={isSubmitting}
              className="rounded-md bg-sky-600 text-white px-4 py-2 text-sm font-semibold hover:bg-sky-700 disabled:opacity-50 shadow-sm"
            >
              {isSubmitting ? 'Zapisywanie…' : 'Zapisz'}
            </button>
          </footer>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

function ValidationFields({
  fieldType, validation, onChange, disabled,
}: { fieldType: FieldType; validation: ValidationRules; onChange: (v: ValidationRules) => void; disabled: boolean }) {
  return (
    <div className="grid grid-cols-2 gap-2 border rounded p-2 bg-slate-50">
      <span className="text-xs uppercase text-slate-500 col-span-2">Walidacja</span>
      {(fieldType === 'NUMBER' || fieldType === 'CURRENCY') && (
        <>
          <NumberInput label="Min" value={validation.min ?? null} onChange={v => onChange({ ...validation, min: v })} disabled={disabled} />
          <NumberInput label="Max" value={validation.max ?? null} onChange={v => onChange({ ...validation, max: v })} disabled={disabled} />
        </>
      )}
      {(fieldType === 'TEXT' || fieldType === 'TEXTAREA' || fieldType === 'RICH_TEXT') && (
        <>
          <NumberInput label="Max długość" value={validation.maxLength ?? null} onChange={v => onChange({ ...validation, maxLength: v })} disabled={disabled} />
          <label className="grid gap-1 col-span-2">
            <span className="text-xs text-slate-500">Regex (opcj.)</span>
            <input
              value={validation.regex ?? ''}
              onChange={e => onChange({ ...validation, regex: e.target.value || null })}
              disabled={disabled}
              className="border rounded px-2 py-1 font-mono"
            />
          </label>
        </>
      )}
    </div>
  )
}

function NumberInput({ label, value, onChange, disabled }: {
  label: string; value: number | null; onChange: (v: number | null) => void; disabled: boolean
}) {
  return (
    <label className="grid gap-1">
      <span className="text-xs text-slate-500">{label}</span>
      <input
        type="number"
        value={value === null ? '' : value}
        onChange={e => onChange(e.target.value === '' ? null : Number(e.target.value))}
        disabled={disabled}
        className="border rounded px-2 py-1"
      />
    </label>
  )
}

function OptionsEditor({ options, onChange, disabled }: {
  options: FieldOption[]; onChange: (o: FieldOption[]) => void; disabled: boolean
}) {
  const updateAt = (i: number, patch: Partial<FieldOption>) => {
    onChange(options.map((o, idx) => idx === i ? { ...o, ...patch } : o))
  }
  return (
    <div className="border rounded p-2 bg-slate-50">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs uppercase text-slate-500">Opcje listy</span>
        <button
          type="button"
          onClick={() => onChange([...options, { value: '', labelPl: '' }])}
          disabled={disabled}
          className="text-xs px-2 py-1 border rounded"
        >
          + Dodaj
        </button>
      </div>
      {options.map((o, i) => (
        <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2 mb-1">
          <input
            value={o.value}
            onChange={e => updateAt(i, { value: e.target.value })}
            disabled={disabled}
            className="border rounded px-2 py-1 font-mono text-xs"
            placeholder="value"
          />
          <input
            value={o.labelPl}
            onChange={e => updateAt(i, { labelPl: e.target.value })}
            disabled={disabled}
            className="border rounded px-2 py-1 text-xs"
            placeholder="Etykieta"
          />
          <button
            type="button"
            onClick={() => onChange(options.filter((_, idx) => idx !== i))}
            disabled={disabled}
            className="text-red-600 text-xs px-2"
          >
            ×
          </button>
        </div>
      ))}
      {options.length === 0 && (
        <p className="text-xs text-slate-400 italic">Brak opcji - dodaj co najmniej jedną.</p>
      )}
    </div>
  )
}

function hasValidation(v: ValidationRules): boolean {
  return v.min != null || v.max != null || (v.regex != null && v.regex !== '') || v.maxLength != null
}

function formatError(err: unknown): string {
  const ax = err as { response?: { data?: { detail?: string; title?: string } } }
  return ax.response?.data?.detail ?? ax.response?.data?.title ?? 'Błąd zapisu'
}
